<template>
  <div>
    <DevicesListLayout :isActionBarOpen="isUserSelectedDevices">
      <PageHeader
        slot="page-header"
        :title="$t('device.list.title_list')"
      />
      <DevicesListToolbar
        v-if="!isDevicesListInvalid"
        slot="devices-list-toolbar"
      />
      <DevicesListContent
        slot="devices-list"
        :devicesListState="getDevicesListState"
      />
      <ActionBar
        slot="devices-list-action-bar"
        :label="numberOfDeviceSelectedLabel"
        :isActionBarOpen="isUserSelectedDevices"
        :actions="availableActionsAccordingToUsersPermissions"
        :hasSeveralMasterAccounts="hasSeveralMasterAccounts"
        fluid
        @clearSelection="clearSelection"
        @applyAction="userSelectionApplyAction"
      />
    </DevicesListLayout>
    <DevicesStatusUpdate
      v-if="selectedDevices.length > 0"
      :display="getUserSelectionDevicesActions.tollingUpdate.enabled"
      :userSelectedDevices="selectedDevices"
      @cancel="clearActions"
      @confirm="handleTollingStatusUpdate"
    />
    <DeviceStatusNotification
      :targetState="targetState"
      :requestResults="requestResults"
      :isNotificationDeviceStatusOpen="isNotificationDeviceStatusOpen"
      @notificationClosed="closeNotificationDeviceStatus"
    />
    <Notification
      :content="notificationContent"
      :type="notificationStatus"
      :isNotificationOpen="isNotificationOpen"
      @notificationClosed="closeNotification"
    />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import moment from 'moment';
import { V2Device } from 'ctp-search-device';
import { TollDomainDetails } from 'ctp-toll-domain';
import { ObuStateCommandRequest } from 'ctp-state';
import Notification from '@/components/common/Notification/Notification.vue';
import ActionBar from '@/components/common/ActionBar/ActionBar.vue';
import { IDevice, IDeviceActions, IDevicesListState, INavigation, store } from '@/store/modules/devices/types';
import { store as userPermissionStore } from '@/store/modules/permissions/types';
import { store as personalisationStore } from '@/store/modules/personalisation/types';
import { store as userStore } from '@/store/modules/user/types';
import { INavigation as INavigationTolldomain, store as tolldomainStore } from '@/store/modules/tollDomain/types';
import PageHeader from '@/components/common/PageHeader/PageHeader.vue';
import DeviceStatusNotification from './components/DevicesStatusNotification/DeviceStatusNotification.vue';
import DevicesListToolbar from './components/DevicesListToolbar/DevicesListToolbar.vue';
import DevicesListLayout from './components/DevicesListLayout/DevicesListLayout.vue';
import DevicesListContent from './components/DevicesListContent/DevicesListContent.vue';
import DevicesStatusUpdate from './components/DevicesStatusUpdate/DevicesStatusUpdate.vue';
import './styles/devices-list.scss';

@Component({
  components: {
    PageHeader,
    DevicesListLayout,
    DevicesListToolbar,
    DevicesListContent,
    DevicesStatusUpdate,
    DeviceStatusNotification,
    ActionBar,
    Notification
  }
})
export default class DevicesList extends Vue {
  private get isUserSelectedDevices() {
    return this.getNumberUserSelectionDevices > 0;
  }

  private get isDevicesListInvalid() {
    return this.getDevicesListState.error.devicesList;
  }

  private get numberOfDeviceSelectedLabel() {
    return `${this.$tc('device.list.selected', this.getNumberUserSelectionDevices)}`;
  }

  @userStore.Getter
  private getSelectedMasterAccount: MasterAccountInfo;

  @store.Action
  private searchDevices!: (configuration: INavigation | void) => Promise<void>;

  @store.Action
  private queryNumberOfActiveDevices!: () => Promise<void>;

  @store.Action
  private userSelectionClearDevices!: () => void;

  @store.Action
  private triggerDeviceUserSelectionAction!: (action: string | undefined) => void;

  @store.Action
  private updateNavigation!: (payload: INavigation) => Promise<void>;

  @store.Action
  private userSelectionResetActions!: () => void;

  @store.Action
  private changeDevicesStateObu!: (payload: any) => Promise<any>;

  @store.Action
  private changeDevicesStateVdo!: (payload: any) => Promise<any>;

  @store.Action
  private devicesListNavigation!: (params: { direction: 'forward' | 'backward' }) => void;

  @personalisationStore.Action
  private associateDevicesToTollDomains!: (payload: any) => Promise<{ notificationStatus: string, code: number }>;

  @tolldomainStore.Action
  private fetchTollDomains!: (configuration: INavigationTolldomain | void) => Promise<void>;

  @tolldomainStore.Action
  private fetchTotalTollDomains!: (payload: any) => Promise<void>;

  @store.Getter
  private getNavigationConfiguration!: INavigation;

  @store.Getter
  private getDevices!: IDevice;

  @store.Getter
  private getDevicesListState!: IDevicesListState;

  @store.Getter
  private getDevicesList!: V2Device[];

  @store.Getter
  private getNumberUserSelectionDevices!: number;

  @store.Getter
  private getUserSelectionDevices!: string[];

  @store.Getter
  private getUserSelectionDevicesActionsNames!: string[];

  @store.Getter
  private getUserSelectionDevicesActions!: { [key: string]: IDeviceActions };

  @userPermissionStore.Getter
  private getCurrentUserIsAbleTo: (readOrWrite: string, featureName: string) => boolean;

  @tolldomainStore.Getter
  private getTotal!: number;

  @tolldomainStore.Getter
  private getTollDomains!: any;

  private actionLaunched = '';

  private isNotificationOpen = false;

  private isNotificationDeviceStatusOpen = false;

  private targetState = '';

  private requestResults: {code: number, message: string}[] = [];

  private notificationContent = '';

  private notificationStatus = '';

  private userDeviceSelectionActionToApply: string | undefined = '';

  private hasSeveralMasterAccounts = false;

  private hasTolldomainsBySelectedDevices = false;

  private tolldomainsBySelectedDevices: TollDomainDetails[] = [];

  @store.Action
  private reinit!: () => void;

  private getCurrentUserIsAbleToWrite(featureName: string) {
    return featureName ? this.getCurrentUserIsAbleTo('write', featureName) : false;
  }

  private get availableActionsAccordingToUsersPermissions() {
    return this.getUserSelectionDevicesActionsNames
      .filter(x => (x === 'device.actions.update_status' && this.getCurrentUserIsAbleToWrite('commandsManagement.write'))
          || (x === 'device.actions.reboot_device' && this.getCurrentUserIsAbleToWrite('commandsManagement.write'))
          || (x === 'device.subscription.title' && this.getCurrentUserIsAbleToWrite('personalisation.write') && this.hasTolldomainsBySelectedDevices));
  }

  private userSelectionApplyAction(payload: string) {
    const actionsObject = this.getUserSelectionDevicesActions;
    this.userDeviceSelectionActionToApply = Object
      .keys(actionsObject)
      .find(x => this.$t(actionsObject[x].label) === payload);
    this.triggerDeviceUserSelectionAction(this.userDeviceSelectionActionToApply);
    if (actionsObject.reboot.enabled) {
      this.handleTollingStatusUpdate({ tollingStatus: 'reboot', tollingReason: [] });
    }
  }

  private clearSelection() {
    this.userSelectionClearDevices();
    this.userSelectionResetActions();
  }

  private clearActions() {
    this.userSelectionResetActions();
  }

  private get selectedDevices() {
    const selectedDevices = this.getDevicesList
      .filter((device: any) => this.getUserSelectionDevices.includes(device.id));
    this.hasSeveralMasterAccounts = selectedDevices.filter((selectedDevice: any) => selectedDevice.masterName !== selectedDevices[0].masterName).length > 0;

    if (selectedDevices && selectedDevices[0]?.masterName) {
      this.tolldomainsBySelectedDevices = this.getTollDomains.filter((tolldomain: TollDomainDetails) => tolldomain.masterName === selectedDevices[0].masterName && tolldomain.type !== 0);
    }
    this.hasTolldomainsBySelectedDevices = this.tolldomainsBySelectedDevices.length > 0;
    return selectedDevices;
  }

  private async handleTollingStatusUpdate(payload: any) {
    const stateCommandRequest: ObuStateCommandRequest[] = [];
    this.selectedDevices.forEach(async (device: V2Device) => {
      stateCommandRequest.push({ deviceId: device.shortDeviceId!, targetState: payload.tollingStatus, reason: payload.tollingReason.length ? payload.tollingReason : undefined });
    });
    let answer;
    if (this.selectedDevices.every(device => device.type === 'obu5gen')) {
      answer = await this.changeDevicesStateObu(stateCommandRequest);
    } else if (this.selectedDevices.every(device => device.type === 'vdolink')) {
      answer = await this.changeDevicesStateVdo(stateCommandRequest);
    } else {
      answer = { message: 'no_action_different_types' };
    }
    try {
      this.requestResults.push({
        ...answer,
        message: answer.message ? answer.message : 'success'
      });
    } catch (err: any) {
      const error = await err;
      this.requestResults.push({
        ...error,
        message: error
      });
    }
    this.isNotificationDeviceStatusOpen = true;
    this.targetState = payload.tollingStatus;
    this.onNavigationChanged();
    this.clearSelection();
  }

  private closeNotification() {
    this.isNotificationOpen = false;
    this.notificationContent = '';
    this.notificationStatus = '';
  }

  private closeNotificationDeviceStatus() {
    this.isNotificationDeviceStatusOpen = false;
    this.requestResults = [];
    this.targetState = '';
  }

  private humanizeDuration = (date: number): string => {
    const now = moment(new Date());
    return `${moment.duration(now.diff(moment(date * 1000))).humanize()}`;
  };

  private unixToDate = (date: number): string => moment(new Date(date * 1000)).format('DD/MM/YYYY HH:mm');

  @Watch('getNavigationConfiguration', { immediate: true, deep: true })
  private onNavigationChanged() {
    if (!this.getDevicesListState.loading.devicesList) {
      this.loadList();
    }
  }

  @Watch('getSelectedMasterAccount', { immediate: true, deep: true })
  private onMasterChange() {
    const account = this.getSelectedMasterAccount;
    if (account.name) {
      this.loadList();
    }
  }

  private async loadList() {
    const account = { masterId: this.getSelectedMasterAccount.id };
    if (account.masterId > 0) {
      this.queryNumberOfActiveDevices();
      // Retrieve savedFilters from ListFilters if they exist
      const savedFilters = sessionStorage.getItem('savedFilters');
      const configFilters: {[key: string]: any} = {};
      if (savedFilters && JSON.parse(savedFilters).selectedFilters.length > 0) {
        JSON.parse(savedFilters).selectedFilters.forEach((item: any) => {
          configFilters[item.id] = item.values;
        });
      }
      if (savedFilters && JSON.parse(savedFilters).multiSearch !== '') {
        configFilters.identifierMatchers = JSON.parse(savedFilters).multiSearch.replace(/ /g, '').split(',');
      }
      if (configFilters) this.getNavigationConfiguration.filters = configFilters;
      await this.searchDevices({ ...this.getNavigationConfiguration, ...account });
      await this.fetchTotalTollDomains({});
      this.fetchTollDomains({
        start: 0,
        domainsPerPage: this.getTotal,
        direction: 'forward',
        isLastDomainPage: false
      });
    }
  }

  private destroyed() {
    this.reinit();
  }
}
</script>
